/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { PersonaSize } from "@fluentui/react";
import { getStrings } from "@microsoft/shifts-experiences-shared";
import { Moment } from "moment";
import { IMemberEntity, IShiftRequestEntity, ShiftRequestStates } from "sh-models";
import { UserStore } from "sh-stores";
import StringsStore from "sh-strings/store";

import DateTimeFormatter, { DateTimeFormatType } from "../../../../../utility/DateTimeFormatter";
import MemberUtils from "../../../../../utility/MemberUtils";
import ShiftRequestUtils from "../../../../../utility/ShiftRequestUtils";
import { IUserPersonaProps } from "../../../../common/IUserPersonaProps";

/**
 * Formats the date as a string.
 * @param date The date to format.
 * @returns The date as a string.
 */
export const getDateTimeAsString = (date: Moment): string|undefined => {
    if (!date) {
        return undefined;
    }
    return DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.DateTime_DateShort_Time);
};

const isCancelledOrAutoDeclined = (request: IShiftRequestEntity): boolean => {
    return request.state === "AutoDeclined" || request.state === "SenderDeclined";
};

/**
 * Retrieves the response message for the request.
 * @param request The shift request entity.
 * @returns String in the format: "Note: {The response message}".
 */
export const getResponseMessage = (request: IShiftRequestEntity): string|undefined => {
    const managerResponse = ShiftRequestUtils.getResponseMessageForOpenShiftRequest(request);

    if (!(managerResponse?.length > 0) || isCancelledOrAutoDeclined(request)) {
        return undefined;
    }

    // TODO (CLM): Make 'note:' bold.
    const managerNote = getStrings("shiftRequests", "managerNote", [managerResponse]);

    return managerNote;
};

/**
 * Retrieves the status message for the request.
 * @example "Approved by Tom Smith".
 * @param request The shift request entity.
 * @param member The member entity who approved/declined the request.
 * @returns The status message for the request.
 */
const getStatusMessage = (request: IShiftRequestEntity, member: IMemberEntity): string => {
    const requestStrings = StringsStore().registeredStringModules.get("shiftRequests").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;
    let statusMessage: string;

     if (request.state === ShiftRequestStates.ManagerApproved) {
         statusMessage = requestStrings.get("approvedBy");
     } else if (request.state === ShiftRequestStates.ManagerDeclined || request.state === ShiftRequestStates.AutoDeclined) {
         statusMessage = requestStrings.get("declinedBy");
    }

    if (member?.userId === UserStore().user?.userId) {
        statusMessage = statusMessage.format(commonStrings.get("you"));
    } else {
        const unknownUserString = getStrings("openShiftsExperience", "unavailableUserNameText");
        statusMessage = statusMessage.format(member?.displayName ?? unknownUserString);
    }

    return statusMessage;
};

/**
 * Retrieves the props for User Persona, with the status message being displayed as the text.
 * @param request The shift request entity.
 * @param member The member entity.
 * @returns The user persona props.
 */
export const getUserPersonaProps = (request: IShiftRequestEntity, member: IMemberEntity): IUserPersonaProps => {
    const responseStatus = getStatusMessage(request, member);

    return {
        userId: member?.userId,
        imageInitials: MemberUtils.getInitials(member),
        size: PersonaSize.size24,
        text: responseStatus
    };
};